/**
 * Account API client — typed wrappers around the customer-portal
 * `/account/*` endpoints.
 *
 * Each helper:
 *   1. Calls the shared axios instance with the documented query params.
 *   2. Unwraps the platform envelope into the typed `data` shape via
 *      `unwrap`/`throwAsApiError` from `_envelope.ts` so callers always
 *      see an `ApiError` on the failure path.
 *   3. Returns the camelCase domain shape — the wire format is the
 *      backend's responsibility.
 *
 * The account dashboard (SF-ACCT-001) is the first consumer; the orders
 * list (SF-ACCT-002) and artwork library (SF-ACCT-005) reuse the same
 * helpers with their own pagination params.
 *
 * SF-ACCT-001.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { OrderLifecycleStatus } from '@dfq/shared/lib/orderStatus';
import type {
  AccountProfile,
  AccountProfileUpdateInput,
  Address,
  AddressInput,
} from '@dfq/shared/types/account';
import type { ApiEnvelope } from '@dfq/shared/types/auth';

/**
 * Single order summary as emitted by `GET /api/v1/account/orders`. The
 * backend serialises monetary values as decimal strings (e.g. `"1570.46"`)
 * — formatting is the consumer's responsibility.
 */
export interface AccountOrderSummary {
  id: number;
  orderNumber: string;
  status: OrderLifecycleStatus;
  /** Decimal string, e.g. `"1570.46"`. */
  subtotal: string;
  /** Decimal string, e.g. `"1570.46"`. */
  total: string;
  itemCount: number;
  /** ISO-8601 datetime, e.g. `"2026-04-02T15:32:00Z"`. */
  placedAt: string;
}

/**
 * Pagination envelope mirrored from `meta` on the platform response. The
 * platform's `meta` block wraps pagination here rather than at the
 * envelope root because it is per-collection.
 */
export interface AccountPaginationMeta {
  total: number;
  page: number;
  limit: number;
  /**
   * Last page number — present on `/account/orders`. Optional because
   * the artwork endpoint may not include it.
   */
  lastPage?: number;
}

export interface AccountOrdersResponse {
  orders: AccountOrderSummary[];
  meta: AccountPaginationMeta;
}

export interface GetAccountOrdersParams {
  /** Page number (1-indexed). Defaults to 1 server-side. */
  page?: number;
  /** Page size. Dashboard passes 5; orders list uses 20. */
  limit?: number;
  /**
   * Order lifecycle status filter. Single-select; one of the ten
   * canonical statuses defined in {@link OrderLifecycleStatus}. The
   * server treats an absent param as "all statuses".
   *
   * SF-ACCT-002.
   */
  status?: OrderLifecycleStatus;
  /**
   * Inclusive lower bound on `placed_at`, ISO-8601 date string
   * (`YYYY-MM-DD`). Treated as midnight UTC server-side.
   *
   * SF-ACCT-002.
   */
  dateFrom?: string;
  /**
   * Inclusive upper bound on `placed_at`, ISO-8601 date string
   * (`YYYY-MM-DD`). Treated as end-of-day UTC server-side.
   *
   * SF-ACCT-002.
   */
  dateTo?: string;
  /**
   * Partial / full order-number search. Case-insensitive substring
   * match on `orders.order_number` server-side. Empty / whitespace-only
   * strings are dropped before the request — the URL never carries a
   * `search=` sentinel.
   *
   * SF-ACCT-002.
   */
  search?: string;
}

/**
 * Strip empty / `undefined` filter values before forwarding to axios so
 * the server sees `?status=shipped` rather than `?status=&search=`.
 * Pure helper — also keeps the request URL identical between repeated
 * fetches when only some filters are active, so HTTP caches stay warm.
 */
function compactOrdersParams(
  params: GetAccountOrdersParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (typeof params.page === 'number') out.page = params.page;
  if (typeof params.limit === 'number') out.limit = params.limit;
  if (params.status) out.status = params.status;
  // `placed_from` / `placed_to` are the names the endpoint reads. Sending
  // `dateFrom` / `dateTo` looked right and was silently dropped, so the order
  // history's date-range filter did nothing.
  if (params.dateFrom) out.placed_from = params.dateFrom;
  if (params.dateTo) out.placed_to = params.dateTo;
  if (params.search && params.search.trim().length > 0) {
    out.search = params.search.trim();
  }
  return out;
}

/**
 * GET `/api/v1/account/orders` — fetch the authenticated customer's
 * orders.
 *
 * Used by two consumers:
 *  - SF-ACCT-001 dashboard preview: `{ limit: 5, page: 1 }`.
 *  - SF-ACCT-002 full history page: `{ limit: 20, page, status?, dateFrom?,
 *    dateTo?, search? }`.
 *
 * Empty / `undefined` filter values are dropped before the axios call
 * (see {@link compactOrdersParams}) so the URL stays clean and the
 * server never sees a sentinel string for "no filter."
 */
export async function getAccountOrders(params: GetAccountOrdersParams = {}): Promise<AccountOrdersResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<AccountOrdersResponse>>(
      '/account/orders',
      { params: compactOrdersParams(params) },
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Validation status reported by the platform for each artwork file.
 * Mirrors `App\Services\Artwork\Enums\ValidationStatus` on the backend.
 */
export type ArtworkValidationStatus = 'pending' | 'passed' | 'failed' | 'manual_review';

/**
 * Order reference attached to an artwork file — every order that
 * currently consumes the artwork is summarised so the library page can
 * link back to each.
 */
export interface AccountArtworkOrderRef {
  id: number;
  orderNumber: string;
}

/**
 * Single artwork entry as emitted by `GET /api/v1/account/artwork`.
 *
 * Two consumer shapes share this type:
 *  - SF-ACCT-001 dashboard reads only the `meta.total` count and ignores
 *    file fields, so the optional fields are tolerated.
 *  - SF-ACCT-005 library page reads the full row and expects every field
 *    documented in the ticket spec to be present on the wire.
 */
export interface AccountArtworkSummary {
  id: number;
  originalFilename: string;
  validationStatus: ArtworkValidationStatus;
  uploadedAt: string;
  /** Bytes — formatting to KB/MB/GB lives in `@dfq/shared/lib/format`. */
  fileSizeBytes?: number;
  /**
   * IANA mime type (e.g. `image/png`, `application/pdf`). Used by the
   * library page to choose between `<img>` thumbnail and an icon.
   */
  mimeType?: string;
  /**
   * Pre-signed S3 URL for raster previews, or `null` for vector /
   * document formats where we render a filetype icon instead.
   */
  thumbnailUrl?: string | null;
  /**
   * Active orders that reference the file. Empty array means the file
   * is not currently linked to any order. Optional on the wire so the
   * dashboard's `limit=1` payload (which doesn't need this field) stays
   * cheap.
   */
  orders?: AccountArtworkOrderRef[];
}

export interface AccountArtworkResponse {
  files: AccountArtworkSummary[];
  meta: AccountPaginationMeta;
}

export interface GetAccountArtworkParams {
  page?: number;
  limit?: number;
}

/**
 * GET `/api/v1/account/artwork` — fetch the authenticated customer's
 * saved artwork. Dashboard reads `meta.total` only (passes `limit: 1`
 * to keep payload minimal); the Artwork Library page (SF-ACCT-005)
 * paginates the full list.
 *
 * Snake/camel gap: the platform may emit pagination under the envelope's
 * `meta.pagination` block rather than inside `data.meta`. We merge both
 * locations so the slice always receives a `meta` with `total`.
 */
export async function getAccountArtwork(params: GetAccountArtworkParams = {}): Promise<AccountArtworkResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<Partial<AccountArtworkResponse>>>('/account/artwork', { params });
    const data = unwrap(response.data, response.status);

    // The platform emits pagination under envelope `meta.pagination` (snake_case).
    // `data.meta` is the in-data block some builds use; we prefer the envelope block.
    const envPagination = response.data.meta?.pagination as
      | { total?: number; page?: number; limit?: number; last_page?: number }
      | undefined;

    const meta: AccountPaginationMeta = data.meta ?? {
      total: envPagination?.total ?? 0,
      page: envPagination?.page ?? params.page ?? 1,
      limit: envPagination?.limit ?? params.limit ?? 1,
      lastPage: envPagination?.last_page,
    };

    return { files: data.files ?? [], meta };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ----------------------------------------------------------------------------
// Payment methods (SF-ACCT-006)
// ----------------------------------------------------------------------------

/**
 * Stripe-derived payment-method shape returned by
 * `GET /api/v1/account/payment-methods`. `id` is the Stripe PaymentMethod
 * ID (`pm_xxx`); the platform never exposes the raw card number, full PAN,
 * or CVV — only the brand + last4 + expiry — so this DTO is safe to render
 * client-side without redaction.
 */
export interface PaymentMethodDTO {
  /** Local `payment_methods.id`. The Stripe `pm_…` token is never sent to customers. */
  id: number;
  /**
   * Stripe-emitted brand string. The `cardBrands` util maps these to
   * display labels and icons; unknown values fall back to `unknown`.
   */
  brand: string;
  /** Last 4 digits of the card. Always 4 chars. */
  last4: string;
  /** 1–12. */
  expMonth: number;
  /** 4-digit year, e.g. 2027. */
  expYear: number;
  isDefault: boolean;
}

export interface AccountPaymentMethodsResponse {
  paymentMethods: PaymentMethodDTO[];
}

export interface AccountPaymentMethodResponse {
  paymentMethod: PaymentMethodDTO;
}

export interface CreatePaymentMethodSetupIntentResponse {
  /** Stripe SetupIntent client secret for `stripe.confirmCardSetup`. */
  clientSecret: string;
}

/**
 * GET `/api/v1/account/payment-methods` — list saved Stripe cards for the
 * authenticated customer.
 */
export async function getAccountPaymentMethods(): Promise<AccountPaymentMethodsResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(
      '/account/payment-methods',
    );
    // The platform emits `data.payment_methods`; the slice reads
    // `paymentMethods`. Without this projection the reducer stores `undefined`
    // and the view crashes on `.length`.
    const data = unwrap(response.data, response.status);
    const methods = (data as { payment_methods?: PaymentMethodDTO[] }).payment_methods;

    return { paymentMethods: Array.isArray(methods) ? methods : [] };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Addresses (SF-ACCT-007)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Response shape for `GET /api/v1/account/addresses` — list endpoint.
 */
export interface AccountAddressesResponse {
  addresses: Address[];
}

/**
 * Response shape for the single-address endpoints (`POST`, `PUT`, and the
 * `setDefault` action). The platform wraps the record under `address`.
 */
export interface AccountAddressResponse {
  address: Address;
}

/**
 * GET `/api/v1/account/addresses` — fetch the customer's saved addresses.
 */
export async function getAccountAddresses(): Promise<AccountAddressesResponse> {
  try {
    const response =
      await apiClient.get<ApiEnvelope<AccountAddressesResponse>>('/account/addresses');
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/** The address form requests validate snake_case keys; the responses are camelCase. */
function toAddressPayload(input: AddressInput): Record<string, string | null> {
  return {
    label: input.label,
    first_name: input.firstName,
    last_name: input.lastName,
    company: input.company,
    line1: input.line1,
    line2: input.line2,
    city: input.city,
    state: input.state,
    zip: input.zip,
    country: input.country,
    phone: input.phone,
  };
}

/**
 * POST `/api/v1/account/addresses` — create a new saved address.
 */
export async function createAccountAddress(
  payload: AddressInput,
): Promise<AccountAddressResponse> {
  try {
    const response = await apiClient.post<ApiEnvelope<AccountAddressResponse>>(
      '/account/addresses',
      toAddressPayload(payload),
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * PUT `/api/v1/account/addresses/:id` — update an existing address.
 */
export async function updateAccountAddress(
  id: number,
  payload: AddressInput,
): Promise<AccountAddressResponse> {
  try {
    const response = await apiClient.put<ApiEnvelope<AccountAddressResponse>>(
      `/account/addresses/${id}`,
      toAddressPayload(payload),
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * DELETE `/api/v1/account/addresses/:id` — soft-delete an address.
 *
 * The platform returns a 204 envelope with `data: null`; this helper
 * resolves with the deleted id so the slice can drop it from state.
 */
export async function deleteAccountAddress(id: number): Promise<{ id: number }> {
  try {
    await apiClient.delete<ApiEnvelope<null>>(`/account/addresses/${id}`);
    return { id };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * PUT `/api/v1/account/addresses/:id/default` — promote an address as the
 * customer's default shipping (or, with `kind: 'billing'`, billing)
 * address. The platform clears the flag from
 * all sibling addresses server-side; this helper returns only the
 * promoted record. The slice mirrors the side effect optimistically.
 */
export async function setDefaultAccountAddress(
  id: number,
  kind: 'shipping' | 'billing' = 'shipping',
): Promise<AccountAddressResponse> {
  try {
    const response = await apiClient.put<ApiEnvelope<AccountAddressResponse>>(
      `/account/addresses/${id}/default`,
      { kind },
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/payment-methods` — create a Stripe SetupIntent for
 * the authenticated customer. The web app then calls
 * `stripe.confirmCardSetup(clientSecret, …)` client-side; raw card data
 * never transits the platform API.
 */
export async function createPaymentMethodSetupIntent(): Promise<CreatePaymentMethodSetupIntentResponse> {
  try {
    const response = await apiClient.post<
      ApiEnvelope<{ setup_intent_client_secret?: string }>
    >('/account/payment-methods');
    // The platform emits snake_case; without this projection Stripe gets `undefined`.
    const data = unwrap(response.data, response.status);
    return { clientSecret: data.setup_intent_client_secret ?? '' };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/payment-methods/sync` — save a card whose SetupIntent
 * `stripe.confirmCardSetup` just confirmed, without waiting for Stripe's
 * `payment_method.attached` webhook. Idempotent with that webhook.
 */
export async function syncAccountPaymentMethod(
  setupIntentId: string,
): Promise<AccountPaymentMethodResponse> {
  try {
    const response = await apiClient.post<
      ApiEnvelope<{ payment_method: PaymentMethodDTO }>
    >('/account/payment-methods/sync', { setup_intent_id: setupIntentId });
    const data = unwrap(response.data, response.status);
    return { paymentMethod: data.payment_method };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * PUT `/api/v1/account/payment-methods/:id/default` — flip the default
 * payment method to `id`. Returns the updated DTO.
 */
export async function setDefaultAccountPaymentMethod(
  id: string,
): Promise<AccountPaymentMethodResponse> {
  try {
    const response = await apiClient.put<
      ApiEnvelope<{ payment_method: PaymentMethodDTO }>
    >(`/account/payment-methods/${encodeURIComponent(id)}/default`);
    const data = unwrap(response.data, response.status);
    return { paymentMethod: data.payment_method };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * DELETE `/api/v1/account/payment-methods/:id` — detach a saved card.
 * The platform 409s if the card is the customer's default and only card.
 */
export async function deleteAccountPaymentMethod(id: string): Promise<void> {
  try {
    await apiClient.delete<ApiEnvelope<null>>(
      `/account/payment-methods/${encodeURIComponent(id)}`,
    );
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * 409 conflict body returned by `DELETE /api/v1/account/artwork/:id`
 * when the file is referenced by one or more active orders. Surfaced
 * via the structured `error.context` slot on the platform envelope.
 */
export interface DeleteAccountArtworkConflict {
  referencedOrderNumbers: string[];
}

/**
 * DELETE `/api/v1/account/artwork/:id` — delete an artwork file owned
 * by the authenticated customer. Returns the file id on success so the
 * caller can confirm the matching record. Throws an `ApiError` for any
 * non-2xx response — callers inspect `err.errorCode` / `err.status` to
 * distinguish 404 (gone) from 409 (referenced by an active order).
 */
export async function deleteAccountArtwork(id: number): Promise<{ id: number }> {
  try {
    const response = await apiClient.delete<ApiEnvelope<unknown>>(`/account/artwork/${id}`);
    // 204 No Content envelopes carry no `data` slot; `unwrap` returns
    // `{}` for those. Either way we surface the deleted id so the caller
    // can mirror the local-state mutation against the right row.
    unwrap(response.data, response.status);
    return { id };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Profile (SF-ACCT-008)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Wire shape returned by `GET /api/v1/account`. The platform spec
 * documents snake_case keys here (see ticket #98); we adapt to camelCase
 * before handing the value to the slice. Keeping the wire shape explicit
 * avoids the snake_case/camelCase question coming up at every call site.
 */
interface AccountProfileWire {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  company_name?: string | null;
  phone?: string | null;
  business_type?: string | null;
  role?: string;
  stripe_customer_id?: string | null;
  is_active?: boolean;
  email_verified_at?: string | null;
  pending_email?: string | null;
  last_login_at?: string | null;
  created_at?: string | null;
  has_stripe_payment_method?: boolean;
}

/**
 * The platform emits `data.profile` with camelCase keys; older fixtures send
 * the profile flat in snake_case. Normalize both to the snake_case wire type.
 */
function profileWire(payload: AccountProfileWire | { profile: Record<string, unknown> }): AccountProfileWire {
  const raw = 'profile' in payload ? payload.profile : payload;
  const snake: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    snake[key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)] = value;
  }
  return snake as unknown as AccountProfileWire;
}

/**
 * Translate the snake_case wire payload into the camelCase
 * {@link AccountProfile} shape consumed by the slice / view. Tolerant of
 * missing optional fields so older platform builds don't crash the page.
 */
function toAccountProfile(payload: AccountProfileWire | { profile: Record<string, unknown> }): AccountProfile {
  const wire = profileWire(payload);
  return {
    id: wire.id,
    firstName: wire.first_name,
    lastName: wire.last_name,
    email: wire.email,
    companyName: wire.company_name ?? null,
    phone: wire.phone ?? null,
    businessType:
      (wire.business_type as AccountProfile['businessType']) ?? null,
    role: wire.role ?? 'customer',
    stripeCustomerId: wire.stripe_customer_id ?? null,
    isActive: wire.is_active ?? true,
    emailVerifiedAt: wire.email_verified_at ?? null,
    pendingEmail: wire.pending_email ?? null,
    lastLoginAt: wire.last_login_at ?? null,
    createdAt: wire.created_at ?? null,
    hasStripePaymentMethod: wire.has_stripe_payment_method ?? false,
  };
}

/**
 * GET `/api/v1/account` — fetch the authenticated customer's full
 * profile. Used by `ProfileView` to hydrate the form on first mount.
 */
export async function getAccountProfile(): Promise<AccountProfile> {
  try {
    const response = await apiClient.get<ApiEnvelope<AccountProfileWire>>(
      '/account',
    );
    const data = unwrap(response.data, response.status);
    return toAccountProfile(data);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * PUT `/api/v1/account` — update the authenticated customer's personal
 * + business profile. The platform validates required fields and returns
 * the updated record; we adapt the wire shape back to {@link AccountProfile}
 * so the slice never sees snake_case.
 *
 * 422 validation envelopes carry per-field errors under `errors`; these
 * surface on the thrown {@link ApiError}'s `errors` map for the form to
 * display inline below each field.
 */
export async function updateAccountProfile(
  payload: AccountProfileUpdateInput,
): Promise<AccountProfile> {
  try {
    const response = await apiClient.put<ApiEnvelope<AccountProfileWire>>(
      '/account',
      payload,
    );
    const data = unwrap(response.data, response.status);
    return toAccountProfile(data);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/email/resend` — re-sends the verification link for the
 * pending (or never-verified) address. Limited to one send a minute; a repeat
 * inside the window throws an `ApiError` with status 429.
 *
 * PL-AUTH-126.
 */
export async function resendAccountEmailVerification(): Promise<void> {
  try {
    const response = await apiClient.post<ApiEnvelope<null>>('/account/email/resend');
    unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * `POST /account/password`. Existing OAuth tokens stay valid after success
 * (the platform does not revoke sessions on change), so the caller keeps its
 * signed-in session. A wrong current password is a 401 `Invalid password`;
 * complexity failures are a 422 keyed under `new_password`.
 */
export async function changeAccountPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  try {
    const response = await apiClient.post<ApiEnvelope<Record<string, never>>>(
      '/account/password',
      { current_password: input.currentPassword, new_password: input.newPassword },
    );
    unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MFA + Trusted Devices (SF-ACCT-009)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Status of the authenticated customer's MFA enrollment, adapted from
 * `GET /api/v1/account/mfa/status` (`data.mfa.enabled` / `configuredAt`). `confirmedAt` is the ISO-8601 datetime the
 * customer confirmed enrollment; `null` when MFA is disabled.
 */
export interface AccountMfaStatus {
  isEnabled: boolean;
  confirmedAt: string | null;
}

/**
 * Server response for `POST /api/v1/account/mfa/enable` — emits the
 * `otpauth://` URI for QR rendering and the base32 secret for manual
 * authenticator app entry. MFA is NOT yet enabled at this point — the
 * customer must confirm via `POST /api/v1/account/mfa/confirm`.
 */
export interface MfaEnrollmentChallenge {
  /** The `otpauth://` provisioning URI — rendered client-side as a QR code. */
  qrCodeUrl: string;
  secretKey: string;
  /** Single-use recovery codes, issued with the secret and shown after confirmation. */
  recoveryCodes: string[];
}

/**
 * Successful response for `POST /api/v1/account/mfa/confirm` — backup
 * codes shown to the customer once and never re-fetchable.
 */
export interface MfaConfirmationResult {
  backupCodes: string[];
}

/**
 * Single trusted-device record as emitted by
 * `GET /api/v1/account/trusted-devices`. The raw `userAgent` is parsed
 * client-side into a friendly label by `@dfq/shared/lib/parseUserAgent`.
 */
export interface TrustedDevice {
  id: number;
  userAgent: string;
  ipAddress: string;
  /** ISO-8601 datetime — the device's trust token expiry (30d from creation). */
  expiresAt: string;
  /** ISO-8601 datetime — when the trust token was issued. */
  createdAt: string;
}

export interface AccountTrustedDevicesResponse {
  devices: TrustedDevice[];
}

/**
 * Wire shape of `TrustedDeviceDTO::toArray()`. `deviceName` holds the raw
 * user-agent string captured when the device was trusted.
 */
interface TrustedDeviceWire {
  id: number;
  deviceName: string;
  lastSeenIp: string | null;
  lastSeenAt: string | null;
  expiresAt: string;
  createdAt: string;
}

interface AccountMfaStatusWire {
  mfa: {
    enabled: boolean;
    method: string | null;
    configuredAt: string | null;
  };
}

interface MfaEnrollmentChallengeWire {
  otpauthUri: string;
  secret: string;
  recoveryCodes: string[];
}

interface AccountTrustedDevicesResponseWire {
  devices: TrustedDeviceWire[];
}

function deviceFromWire(w: TrustedDeviceWire): TrustedDevice {
  return {
    id: w.id,
    userAgent: w.deviceName,
    ipAddress: w.lastSeenIp ?? '',
    expiresAt: w.expiresAt,
    createdAt: w.createdAt,
  };
}

function mfaStatusFromWire(wire: AccountMfaStatusWire): AccountMfaStatus {
  return {
    isEnabled: wire.mfa.enabled,
    confirmedAt: wire.mfa.configuredAt,
  };
}

/**
 * GET `/api/v1/account/mfa/status` — fetch MFA enrollment status for the
 * authenticated customer.
 */
export async function getAccountMfaStatus(): Promise<AccountMfaStatus> {
  try {
    // The platform serves status at /account/mfa/status; a bare GET on
    // /account/mfa is DELETE-only and returned 405 on every account page load.
    const response = await apiClient.get<ApiEnvelope<AccountMfaStatusWire>>('/account/mfa/status');
    const wire = unwrap(response.data, response.status);
    return mfaStatusFromWire(wire);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/mfa/enable` — initiate MFA enrollment. Returns
 * an `otpauth://` URI for QR rendering and the plain-text secret. The
 * caller must follow up with `confirmAccountMfa` to complete enrollment.
 */
export async function enableAccountMfa(): Promise<MfaEnrollmentChallenge> {
  try {
    const response = await apiClient.post<ApiEnvelope<MfaEnrollmentChallengeWire>>(
      '/account/mfa/enable',
    );
    const wire = unwrap(response.data, response.status);
    return {
      qrCodeUrl: wire.otpauthUri,
      secretKey: wire.secret,
      recoveryCodes: wire.recoveryCodes,
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * POST `/api/v1/account/mfa/confirm` — confirm MFA enrollment with a
 * 6-digit TOTP code. The recovery codes were issued by `enableAccountMfa`;
 * the response carries only the new enrollment status.
 */
export async function confirmAccountMfa(code: string): Promise<AccountMfaStatus> {
  try {
    const response = await apiClient.post<ApiEnvelope<AccountMfaStatusWire>>(
      '/account/mfa/confirm',
      { code },
    );
    return mfaStatusFromWire(unwrap(response.data, response.status));
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * DELETE `/api/v1/account/mfa` — disable MFA. Requires a valid current
 * TOTP code in the request body to confirm intent. Side effect: the
 * platform invalidates all trusted-device tokens for the user.
 */
export async function disableAccountMfa(code: string): Promise<void> {
  try {
    await apiClient.delete<ApiEnvelope<unknown>>('/account/mfa', {
      data: { code },
    });
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * GET `/api/v1/account/trusted-devices` — list active trusted devices
 * for the authenticated customer.
 */
export async function getAccountTrustedDevices(): Promise<AccountTrustedDevicesResponse> {
  try {
    const response = await apiClient.get<ApiEnvelope<AccountTrustedDevicesResponseWire>>(
      '/account/trusted-devices',
    );
    const wire = unwrap(response.data, response.status);
    return {
      devices: wire.devices.map(deviceFromWire),
    };
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * DELETE `/api/v1/account/trusted-devices/:id` — revoke a single
 * trusted device. The next login from that device will require MFA
 * again.
 */
export async function revokeAccountTrustedDevice(id: number): Promise<{ id: number }> {
  try {
    await apiClient.delete<ApiEnvelope<null>>(`/account/trusted-devices/${id}`);
    return { id };
  } catch (err) {
    throwAsApiError(err);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Single-order detail (SF-ACCT-003)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Address snapshot stored on an order at placement time. Mirrors the
 * platform's `shippingAddressSnapshot` field shape — a frozen copy of the
 * address fields so the detail page renders the destination as it was at
 * order time, not the customer's current default.
 */
export interface AccountOrderAddressSnapshot {
  firstName: string;
  lastName: string;
  company: string | null;
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/**
 * Single-applied-discount entry inside `appliedDiscounts`. The platform
 * emits both coupon and promotion rows in the same shape; the `type`
 * discriminator drives how the UI labels the line.
 */
export interface AccountOrderAppliedDiscount {
  type: 'coupon' | 'promotion';
  code: string | null;
  description: string;
  /** Decimal string. Always positive; the UI prefixes a `−`. */
  amount: string;
}

/**
 * Status-history entry — one record per lifecycle transition. The
 * detail-page timeline iterates over this list and renders a step per
 * entry, with the current status visually highlighted.
 */
export interface AccountOrderStatusHistoryEntry {
  fromStatus: OrderLifecycleStatus | null;
  toStatus: OrderLifecycleStatus;
  /** ISO-8601 datetime. */
  changedAt: string;
  /** Optional staff note attached to the transition. */
  notes: string | null;
}

/**
 * Snapshot of the product as it existed at order time. Stored as JSON on
 * the order so the detail page renders the configuration accurately even
 * if the live product has since been edited.
 */
export interface AccountOrderProductSnapshot {
  name: string;
  skuPrefix: string;
}

export interface AccountOrderMaterialSnapshot {
  name: string;
  manufacturer: string;
}

export interface AccountOrderOptionSnapshot {
  groupName: string;
  selectedLabel: string;
  /** Decimal string. May be negative (subtractive option) or zero. */
  priceModifier: string;
}

export interface AccountOrderVehicleSnapshot {
  make: string;
  model: string;
  year: number;
  trim: string;
  totalSqft: number;
}

export interface AccountOrderDimensionsSnapshot {
  widthInches: number | null;
  heightInches: number | null;
  quantitySqft: number;
}

export interface AccountOrderAddOnSnapshot {
  name: string;
  /** Decimal string. */
  price: string;
}

/**
 * Sizing mode encoded on each line item. Tracks how the customer chose
 * the dimensions (vehicle lookup vs custom width/height vs predefined
 * size vs fixed product size) so the detail page can render the right
 * snapshot.
 */
export type OrderItemSizingMode =
  | 'vehicle_lookup'
  | 'custom_dimensions'
  | 'predefined_size'
  | 'fixed_flat';

/**
 * Validation status of a single artwork file linked to an order item.
 * Mirrors {@link ArtworkValidationStatus}; re-exported here as the
 * order-detail page consumes the same union via the per-item file list.
 */
export type AccountOrderItemArtworkFile = {
  id: number;
  originalFilename: string;
  validationStatus: ArtworkValidationStatus;
  /** Pre-signed S3 URL — render as `<a href download>`. */
  downloadUrl: string;
};

export interface AccountOrderItem {
  id: number;
  /**
   * Foreign-key id of the source product. Required by the SF-ACCT-004
   * reorder flow when reconstructing a `POST /api/v1/cart` body. Optional
   * because legacy responses may not yet include it; an item missing
   * `productId` cannot be reordered (the thunk classifies it as
   * unrestorable).
   */
  productId?: number;
  productSnapshot: AccountOrderProductSnapshot;
  /** Material foreign key — same caveat as {@link productId}. */
  materialId?: number | null;
  materialSnapshot: AccountOrderMaterialSnapshot;
  optionsSnapshot: AccountOrderOptionSnapshot[];
  /**
   * Raw option-group → selected value(s) map mirroring the platform's
   * `options_snapshot_json`. Used by the reorder flow to rebuild the
   * cart-add request body. Optional for the same reason as the FK ids.
   */
  optionsPayload?: Record<string, string | string[]>;
  /** Vehicle foreign key — present only when sizingMode = vehicle_lookup. */
  vehicleId?: number | null;
  vehicleSnapshot: AccountOrderVehicleSnapshot | null;
  sizingMode: OrderItemSizingMode;
  /** Predefined-size FK — present only when sizingMode = predefined_size. */
  selectedSizeId?: number | null;
  dimensionsSnapshot: AccountOrderDimensionsSnapshot | null;
  addOnsSnapshot: AccountOrderAddOnSnapshot[];
  /** Decimal string. */
  unitPrice: string;
  /** Decimal string. */
  lineTotal: string;
  /** Quantity at order time. */
  quantity?: number;
  artworkFiles: AccountOrderItemArtworkFile[];
}

export type OrderShipmentCarrier = 'ups' | 'fedex' | 'usps';

export interface AccountOrderShipment {
  carrier: OrderShipmentCarrier | null;
  trackingNumber: string | null;
  estimatedDeliveryAt: string | null;
  shippedAt: string | null;
}

/**
 * Full single-order detail emitted by `GET /api/v1/account/orders/:id`.
 * The detail page renders against this DTO directly; the slice stores
 * only one `currentOrder` at a time, isolated from the list state.
 */
export interface AccountOrderDetail {
  id: number;
  orderNumber: string;
  status: OrderLifecycleStatus;
  paymentStatus: 'pending' | 'paid' | 'refunded' | 'partially_refunded';
  /** ISO-8601 datetime. */
  placedAt: string;
  paidAt: string | null;
  /** Decimal string. */
  subtotal: string;
  /** Decimal string. Total of all `appliedDiscounts.amount` values. */
  discountTotal: string;
  /** Decimal string. */
  tax: string;
  /** Decimal string. */
  shippingCost: string;
  /** Decimal string. */
  total: string;
  shippingAddressSnapshot: AccountOrderAddressSnapshot;
  appliedDiscounts: AccountOrderAppliedDiscount[];
  statusHistory: AccountOrderStatusHistoryEntry[];
  items: AccountOrderItem[];
  shipment: AccountOrderShipment | null;
}

/**
 * GET `/api/v1/account/orders/:id` — fetch a single order belonging to
 * the authenticated customer. Used by the SF-ACCT-003 detail page.
 *
 * The platform returns a single `order` envelope (not a list), so the
 * caller hands the parsed numeric id and receives a fully-hydrated
 * {@link AccountOrderDetail}. 404 envelopes are surfaced as `ApiError`
 * — the view distinguishes that from a bad numeric param (handled
 * client-side, no fetch dispatched at all).
 */
export async function getAccountOrder(id: number): Promise<AccountOrderDetail> {
  try {
    const response = await apiClient.get<ApiEnvelope<AccountOrderDetail>>(
      `/account/orders/${id}`,
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/** One order line restored into the cart by {@link reorderAccountOrder}. */
export interface ReorderAddedLine {
  order_item_id: number;
  name: string;
  cart_item_id: number;
}

/** One order line the platform could not put back into the cart. */
export interface ReorderUnrestorableLine {
  order_item_id: number;
  name: string;
  reason: string;
  reason_code: string;
}

export interface ReorderAccountOrderResult {
  added: ReorderAddedLine[];
  unrestorable: ReorderUnrestorableLine[];
}

/**
 * POST `/api/v1/account/orders/:id/reorder` — the platform rebuilds the
 * order's lines in the caller's cart through the normal add-to-cart
 * pipeline and reports which lines were added and which could not be.
 */
export async function reorderAccountOrder(
  id: number,
): Promise<ReorderAccountOrderResult> {
  try {
    const response = await apiClient.post<ApiEnvelope<ReorderAccountOrderResult>>(
      `/account/orders/${id}/reorder`,
    );
    return unwrap(response.data, response.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

/**
 * Convenience grouping export so consumers can `import { accountApi }`
 * if they prefer the namespace style.
 */
export const accountApi = {
  getAccountOrders,
  getAccountOrder,
  reorderAccountOrder,
  getAccountArtwork,
  deleteAccountArtwork,
  getAccountPaymentMethods,
  createPaymentMethodSetupIntent,
  syncAccountPaymentMethod,
  setDefaultAccountPaymentMethod,
  deleteAccountPaymentMethod,
  getAccountAddresses,
  createAccountAddress,
  updateAccountAddress,
  deleteAccountAddress,
  setDefaultAccountAddress,
  getAccountProfile,
  updateAccountProfile,
  changeAccountPassword,
  getAccountMfaStatus,
  enableAccountMfa,
  confirmAccountMfa,
  disableAccountMfa,
  getAccountTrustedDevices,
  revokeAccountTrustedDevice,
};
